// API client with multi-tenant support

import { tenantService } from './tenantService';

// Fallback matches local Django dev server (runserver on :8000, urls mounted at /api).
// Prod builds override via .env.production = https://be.upsynq.com/api.
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000/api';

interface RequestOptions extends RequestInit {
  skipTenant?: boolean;
}

class ApiClient {
  private baseUrl: string;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  /**
   * Build URL — tenant is sent via X-Tenant-Code header, never in the path.
   * Django's TenantSelectorMiddleware resolves the tenant from that header.
   */
  private buildUrl(endpoint: string, _skipTenant = false): string {
    return `${this.baseUrl}${endpoint}`;
  }

  /**
   * Get auth token from storage
   */
  private getAuthToken(): string | null {
    return localStorage.getItem('auth_token');
  }

  /**
   * Build headers with auth token
   */
  private buildHeaders(customHeaders?: HeadersInit): Headers {
    const headers = new Headers({
      'Content-Type': 'application/json',
      ...customHeaders,
    });

    const token = this.getAuthToken();
    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }

    const tenantCode = tenantService.getTenantCode();
    if (tenantCode) {
      headers.set('X-Tenant-Code', tenantCode);
    }

    return headers;
  }

  /**
   * Handle API response
   */
  private async handleResponse<T>(response: Response): Promise<T> {
    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Request failed' }));
      // Surface the BE's most-specific error first. Many DRF views
      // return `{error, detail, issues}` (e.g. study run validation
      // → `{error, issues}`). Fold those into the thrown message so
      // callers can `toast.error(err.message)` and get something
      // actionable instead of "HTTP 400".
      let msg: string =
        error.detail
        || error.message
        || error.error
        || `HTTP ${response.status}`;
      if (Array.isArray(error.issues) && error.issues.length) {
        msg = `${msg}: ${error.issues.join(" · ")}`;
      }
      const e: any = new Error(msg);
      e.status = response.status;
      e.body = error;
      throw e;
    }

    // Return empty for 204 No Content
    if (response.status === 204) {
      return null as unknown as T;
    }

    // Check content length to be safe (optional but good practice)
    const contentLength = response.headers.get('content-length');
    if (contentLength === '0') {
      return null as unknown as T;
    }

    return response.json();
  }

  /**
   * Internal request method with auto-refresh mechanism
   */
  private async request<T>(endpoint: string, options: RequestOptions & { method: string, body?: any, _retry?: boolean }): Promise<T> {
    const { skipTenant, headers: customHeaders, method, body, _retry, ...fetchOptions } = options;
    const url = this.buildUrl(endpoint, skipTenant);
    let headers = this.buildHeaders(customHeaders as HeadersInit);

    // Prepare content-type for JSON
    const isFormData = body instanceof FormData;
    if (isFormData) {
      headers.delete('Content-Type');
    }

    const config: RequestInit = {
      method,
      headers,
      body: isFormData ? (body as FormData) : (body ? JSON.stringify(body) : undefined),
      credentials: 'include',
      ...fetchOptions,
    };

    let response = await fetch(url, config);

    // Handle 401 Unauthorized with Token Refresh.
    //
    // Refresh token now lives in an HttpOnly cookie set by the BE at
    // login (apps/control/auth_views.py UpSynQTokenObtainPairView). The
    // browser sends it automatically on credentialed requests; the FE
    // never has direct access. We just POST to /auth/refresh/ with no
    // body and let the cookie do its job.
    if (response.status === 401 && !_retry) {
      try {
        const refreshUrl = `${this.baseUrl}/auth/refresh/`;
        const refreshResponse = await fetch(refreshUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: '{}',
        });

        if (refreshResponse.ok) {
          const refreshData = await refreshResponse.json();
          const newAccessToken = refreshData.access || refreshData.token;
          if (newAccessToken) {
            localStorage.setItem('auth_token', newAccessToken);
            // refresh token deliberately NOT mirrored back to JS — it
            // stays in the HttpOnly cookie so XSS can't steal it.
            headers.set('Authorization', `Bearer ${newAccessToken}`);
            const retryConfig = { ...config, headers };
            response = await fetch(url, retryConfig);
          }
        } else {
          // Refresh failed (cookie missing/expired) — clean up local
          // session bits so guards re-route to login.
          localStorage.removeItem('auth_token');
          localStorage.removeItem('refresh_token');
          localStorage.removeItem('user');
        }
      } catch (refreshError) {
        console.error("Token refresh error", refreshError);
      }
    }

    return this.handleResponse<T>(response);
  }

  /**
   * GET request
   */
  async get<T>(endpoint: string, options: RequestOptions = {}): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'GET' });
  }

  /**
   * POST request
   */
  async post<T>(endpoint: string, data?: unknown, options: RequestOptions = {}): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'POST', body: data as any });
  }

  /**
   * PUT request
   */
  async put<T>(endpoint: string, data?: unknown, options: RequestOptions = {}): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'PUT', body: data as any });
  }

  /**
   * PATCH request
   */
  async patch<T>(endpoint: string, data?: unknown, options: RequestOptions = {}): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'PATCH', body: data as any });
  }

  /**
   * DELETE request
   */
  async delete<T>(endpoint: string, options: RequestOptions = {}): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'DELETE' });
  }
}

export const apiClient = new ApiClient(API_BASE_URL);
